import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import request from "../../../api/request";
import { KdOrderStatus, OrderStatus } from "../../../const/enum";
import { AuthContext } from "../../../auth/authContext";
import FilterPanel, {
  FilterOption,
  QueryOptionsItem,
} from "../../../component/FilterPanel/FilterPanel";
import "./ListPage.css";
import { Order } from "../../../const/model";
import OrderDetail from "../../../component/OrderDetail/OrderDetail";
import { BackendAPI } from "../../../const/api";
import { getOrderQueryOptions } from "../../../services";
import { useToast } from "../../../component/ToastBar";
import { Dialogs } from "../../../component/Dialogs";

export interface QueryOptions {
  materials: QueryOptionsItem[];
  customers: QueryOptionsItem[];
  salespersons: QueryOptionsItem[];
}

const OrderList = () => {
  const toast = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [filters, setFilters] = useState<any>(); // 筛选条件
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [queryOptions, setQueryOptions] = useState<QueryOptions>({
    materials: [],
    customers: [],
    salespersons: [],
  });
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]); // 选中的订单id
  const [currentPageAllSelected, setCurrentPageAllSelected] = useState(false); // 当前页是否全选
  const authContext = useContext(AuthContext);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialogTitle, setDialogTitle] = useState<string>("");
  const [dialogContent, setDialogContent] = useState<string>("");
  const handleConfirm = useRef<any>();
  const [onlyShowMine, setOnlyShowMine] = useState<boolean>(false);
  const [onlyUpload, setOnlyUpload] = useState<boolean>(false);

  // 登录信息就绪后再初始化 filters，避免首屏请求不带筛选条件
  useEffect(() => {
    if (!authContext) {
      return;
    }
    setFilters((prev) => {
      if (prev !== undefined) {
        return prev;
      }
      if (authContext.isWarehouse()) {
        return { statuses: [OrderStatus.REVIEWED] };
      }
      return {};
    });
    getOrderQueryOptions(
      authContext.isCustomer(),
      authContext.isAdmin() || authContext.isFinance(),
    ).then(([materials, customers, salespersons]) =>
      setQueryOptions({ materials, customers, salespersons }),
    );
  }, [authContext]);

  // 更新列表or选择/取消单行时重新计算全选状态
  useEffect(() => {
    setCurrentPageAllSelected(
      selectedOrderIds.length > 0 &&
        orders.every((order) => selectedOrderIds.includes(order.id)),
    );
  }, [selectedOrderIds, orders]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // 重置到第一页
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const fetchOrders = useCallback(async () => {
    if (authContext) {
      try {
        const requestFilters = { ...filters };
        // 清空状态筛选时不传 statuses，后端对仓库管理员返回全部可见状态
        if (
          Array.isArray(requestFilters.statuses) &&
          requestFilters.statuses.length === 0
        ) {
          delete requestFilters.statuses;
        }
        const params = {
          page: page + 1,
          size: rowsPerPage,
          sort: sortConfig.key
            ? `${sortConfig.key},${sortConfig.direction}`
            : "",
          onlyShowMine,
          ...requestFilters,
        };
        let get_data;
        // no 'materials' key in params
        const { materials, customers, ...rest } = params;
        if (authContext.isCustomer() || authContext.isSalesScopeRole()) {
          get_data = await request.get(BackendAPI.orders, rest);
        } else {
          get_data = await request.get(BackendAPI.adminOrders, rest);
        }
        return get_data.data;
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    } else {
      console.error("Auth context not ready");
    }
  }, [
    authContext,
    filters,
    onlyShowMine,
    page,
    rowsPerPage,
    sortConfig.direction,
    sortConfig.key,
  ]);

  const updateOrders = useCallback(() => {
    setOrders([]);
    fetchOrders().then((data) => {
      setOrders(data.orders);
      setTotalCount(data.total);
    });
  }, [fetchOrders]);

  const updateOrder = useCallback(
    (orderId) => {
      request.get(BackendAPI.getOrder(orderId)).then((res) => {
        if (res.success) {
          const newOrders = orders.map((order) =>
            order.id === orderId ? res.data : order,
          );
          setOrders(newOrders);
        }
      });
    },
    [orders],
  );

  useEffect(() => {
    if (!authContext || filters === undefined) {
      return;
    }
    updateOrders();
  }, [authContext, page, rowsPerPage, sortConfig, filters, onlyShowMine, updateOrders]);

  useEffect(() => {
    setSelectedOrderIds([]);
    setSelectedOrder(null);
  }, [filters, onlyShowMine, orders]);

  // 处理筛选变化
  const onFilterChange = (newFilters) => {
    const material_ids =
      newFilters["materials"]?.map((m) => m.data["id"]) || [];
    const customer_ids =
      newFilters["customer_ids"]?.map((c) => c.data["id"]) || [];
    const salespersons = newFilters["salespersons"]?.map((c) => c.label) || [];
    setFilters({
      ...newFilters,
      material_ids,
      customer_ids,
      salespersons,
    });
    setPage(0); // 重置到第一页
  };

  const onBatchExport = async (newFilters) => {
    const material_ids =
      newFilters["materials"]?.map((m) => m.data["id"]) || [];
    const customer_ids =
      newFilters["customers"]?.map((c) => c.data["id"]) || [];
    const salespersons = newFilters["salespersons"]?.map((c) => c.label) || [];
    if (authContext) {
      try {
        const params = {
          sort: sortConfig.key
            ? `${sortConfig.key},${sortConfig.direction}`
            : "",
          onlyShowMine,
          ...newFilters,
          material_ids,
          customer_ids,
          salespersons,
        };
        const { materials, customers, ...rest } = params;
        const responseBlob = await request.post(
          BackendAPI.batchOrderExport,
          rest,
          undefined,
          undefined,
        );
        const url = window.URL.createObjectURL(responseBlob);
        window.open(url, "_blank");
      } catch (error) {
        console.error("Error batch exporting orders:", error);
      }
    } else {
      console.error("Auth context not ready");
    }
  };

  const filterOptions = useMemo(() => {
    const options: FilterOption[] = [
      { key: "order_id", label: "订单号", type: "text" },
      { key: "tracking_number", label: "物流单号", type: "text" },
      {
        key: "materials",
        label: "物料",
        type: "multiselect",
        options: queryOptions.materials,
      },
      {
        key: "statuses",
        label: "状态",
        type: "multiselect",
        options: Object.values(OrderStatus).map(
          (status: OrderStatus) => status,
        ),
        defaultValue: authContext?.isWarehouse()
          ? [
              OrderStatus.REVIEWED,
            ]
          : [],
      },
      {
        key: "kdStatus",
        label: "金蝶状态",
        type: "select",
        options: Object.values(KdOrderStatus).map(
          (status: KdOrderStatus) => status,
        ),
      },
      {
        key: "billingName",
        label: "收件人",
        type: "text",
      },
      {
        key: "billingTel",
        label: "手机号",
        type: "text",
      },
    ];
    if (authContext && (authContext.isWarehouse() || authContext.isBA())) {
      options.splice(
        2,
        0,
        {
          key: "salespersons",
          label: "业务员",
          type: "multiselect",
          options: queryOptions.salespersons,
        },
        {
          key: "customer_name",
          label: "客户名称",
          type: "text",
        },
      );
    }
    if (authContext && (authContext.isAdmin() || authContext.isFinance())) {
      options.splice(
        2,
        0,
        {
          key: "salespersons",
          label: "业务员",
          type: "multiselect",
          options: queryOptions.salespersons,
        },
        {
          key: "customer_ids",
          label: "客户",
          type: "multiselect",
          options: queryOptions.customers,
        },
      );
    }
    return options;
  }, [authContext, queryOptions]);

  const handleCheckboxChange = (orderId: string) => {
    setSelectedOrderIds((prevSelectedOrderIds) => {
      let newSelectedOrderIds = [...prevSelectedOrderIds];
      if (newSelectedOrderIds.includes(orderId)) {
        newSelectedOrderIds = newSelectedOrderIds.filter(
          (id) => id !== orderId,
        );
      } else {
        newSelectedOrderIds.push(orderId);
      }
      return newSelectedOrderIds;
    });
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseDetail = (changed: boolean) => {
    setSelectedOrder(null);
    if (changed) {
      updateOrders();
    }
  };

  const handleAuditOrder = useCallback(
    (orderId) => {
      request.put(BackendAPI.auditOrder(orderId), {}).then((res) => {
        if (res.success) {
          updateOrder(orderId);
          toast("审核成功");
        }
      });
    },
    [toast, updateOrder],
  );

  const currentPageOrders = useMemo(() => {
    return orders.map((order) => {
      return (
        <TableRow
          key={order.id}
          style={{
            backgroundColor:
              selectedOrder && selectedOrder.id === order.id
                ? "#f0f0f0"
                : "transparent",
          }}
        >
          <TableCell className="column-width-medium">
            <input
              className="checkbox"
              type="checkbox"
              checked={selectedOrderIds.includes(order.id)}
              onChange={() => handleCheckboxChange(order.id)}
            />
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleOrderClick(order);
              }}
            >
              详情
            </Button>
            {authContext && (authContext.isAdmin() || authContext.isBA()) && (
              <Button
                disabled={order.status !== OrderStatus.PLACED}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAuditOrder(order.id);
                }}
              >
                审核
              </Button>
            )}
          </TableCell>
          <TableCell className="column-width-medium">
            <Tooltip title={order.id}>
              <div>{order.id}</div>
            </Tooltip>
          </TableCell>
          <TableCell className="column-width-small">
            <Tooltip title={order.salesperson}>
              <div>{order.salesperson}</div>
            </Tooltip>
          </TableCell>
          {authContext && !authContext.isCustomer() && (
            <TableCell className="column-width-large">
              <Tooltip title={order.customerName}>
                <div>{order.customerName}</div>
              </Tooltip>
            </TableCell>
          )}
          <TableCell className="column-width-medium">
            <Tooltip title={order.materialModel}>
              <div>{order.materialModel}</div>
            </Tooltip>
          </TableCell>
          <TableCell className="column-width-small">
            <Tooltip title={order.quantity}>
              <div>{order.quantity}</div>
            </Tooltip>
          </TableCell>
          {!authContext?.isWarehouse() && (
            <TableCell className="column-width-small">
              <Tooltip title={order.unitPrice}>
                <div>{order.unitPrice}</div>
              </Tooltip>
            </TableCell>
          )}
          <TableCell className="column-width-small">
            <Tooltip title={order.carrier}>
              <div>{order.carrier}</div>
            </Tooltip>
          </TableCell>
          <TableCell className="column-width-medium">
            <Tooltip title={order.trackingNumber}>
              <div>{order.trackingNumber}</div>
            </Tooltip>
          </TableCell>
          <TableCell className="column-width-small">
            <div>{order.status}</div>
          </TableCell>
          <TableCell className="column-width-medium">
            <Tooltip title={order.materialName}>
              <div>{order.materialName}</div>
            </Tooltip>
          </TableCell>
          <TableCell className="column-width-small">
            <Tooltip title={order.billingName || ""}>
              <div>{order.billingName || ""}</div>
            </Tooltip>
          </TableCell>
          <TableCell className="column-width-medium">
            <Tooltip title={order.billingTel || ""}>
              <div>{order.billingTel || ""}</div>
            </Tooltip>
          </TableCell>
          <TableCell className="column-width-medium">
            <Tooltip title={order.createdAt}>
              <div>{order.createdAt}</div>
            </Tooltip>
          </TableCell>
          <TableCell className="column-width-small">
            <div>{order.kdStatus}</div>
          </TableCell>
        </TableRow>
      );
    });
  }, [authContext, orders, selectedOrderIds, selectedOrder, handleAuditOrder]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleExportOrders = async (warehouse_format: boolean) => {
    try {
      const body = {
        orderIds: selectedOrderIds,
        warehouse_format,
      };
      const responseBlob = await request.post(
        BackendAPI.orderExport,
        body,
        undefined,
        undefined,
      );
      const url = window.URL.createObjectURL(responseBlob);
      const newWindow = window.open(url, "_blank");
      // 仓库role: 监听下载完成事件，触发下载完成后的回调函数，询问是否需要将下载的订单设为仓库已录入
      if (authContext?.isWarehouse() && selectedOrderIds.length > 0) {
        if (
          selectedOrderIds.every((id) => {
            const order = orders.find((order) => order.id === id);
            return order && OrderStatus.REVIEWED === order.status;
          })
        ) {
          newWindow?.addEventListener("beforeunload", () => {
            handleEnterOrders();
          });
        } else {
          setShowDialog(true);
          setDialogTitle("订单包含非已审核订单，请注意不要重复打单或发货");
          setDialogContent(
            "本次下载的订单将不会标记为仓库已录入，如需录入请单独下载已审核订单",
          );
          handleConfirm.current = () => setShowDialog(false);
        }
      }
      setSelectedOrderIds([]);
      setSelectedOrder(null);
    } catch (error) {
      console.error("Error exporting orders:", error);
    }
  };

  const handleEnterOrders = async () => {
    const body = {
      orderIds: selectedOrderIds,
    };
    await request.post(BackendAPI.ordersWarehouseEnter, body).then((res) => {
      if (res.success) {
        updateOrders();
        toast("订单已录入仓库");
      }
    });
  };

  const handleClickCancelOrders = () => {
    setShowDialog(true);
    setDialogTitle("确认取消订单?");
    setDialogContent("");
    handleConfirm.current = handleCancelOrders;
  };

  const handleFetchKdStatus = async () => {
    setLoading(true);
    await request
      .post(BackendAPI.refreshKdStatus, {
        orderIds: orders.map((order) => order.id),
      })
      .then((res) => {
        if (res.success) {
          updateOrders();
          toast("金蝶状态刷新成功");
        }
        setLoading(false);
      });
  };

  const handleCancelOrders = async () => {
    const body = {
      orderIds: selectedOrderIds,
    };
    await request.put(BackendAPI.cancelOrder, body).then((res) => {
      if (res.success) {
        updateOrders();
        toast("订单取消成功");
        setSelectedOrderIds([]);
      }
      setShowDialog(false);
    });
  };

  const handleShipOrders = useCallback(async () => {
    const body = {
      orderIds: selectedOrderIds,
    };
    await request.put(BackendAPI.shipOrders, body).then((res) => {
      if (res.success) {
        const shipped = res.data?.shipped || [];
        const failed = res.data?.failed || [];
        updateOrders();
        if (failed.length > 0) {
          toast(`发货成功${shipped.length}单，失败${failed.length}单`, "error");
        } else {
          toast(`发货成功${shipped.length}单`);
        }
        setSelectedOrderIds([]);
      }
      setShowDialog(false);
    });
  }, [selectedOrderIds, toast, updateOrders]);

  const handleClickShipOrders = useCallback(() => {
    setShowDialog(true);
    setDialogTitle("确认发货所选订单?");
    setDialogContent("将把所选订单状态改为已发货（需已填写完整物流信息）");
    handleConfirm.current = handleShipOrders;
  }, [handleShipOrders]);

  const handleUploadKdOrders = useCallback(async () => {
    const body = {
      orderIds: selectedOrderIds,
      onlyUpload,
    };
    setLoading(true);
    await request.put(BackendAPI.uploadKdOrders, body).then((res) => {
      if (res.success) {
        updateOrders();
        if (res.message) {
          alert(`订单部分上传成功，存在错误：${res.message}`);
        } else {
          toast("订单上传金蝶成功");
        }
      }
      setLoading(false);
      setShowDialog(false);
    });
  }, [onlyUpload, selectedOrderIds, toast, updateOrders]);

  const handleClickUploadKdOrders = useCallback(() => {
    setShowDialog(true);
    setDialogTitle("确定上传/提交所选订单?");
    setDialogContent("");
    handleConfirm.current = handleUploadKdOrders;
  }, [handleUploadKdOrders]);

  const handleAllSelectChange = () => {
    const selectedOrderIdsSet = new Set(selectedOrderIds);
    const orderIds = orders.map((order) => order.id);
    if (currentPageAllSelected) {
      orderIds.forEach((id) => selectedOrderIdsSet.delete(id));
    } else {
      orderIds.forEach((id) => selectedOrderIdsSet.add(id));
    }
    setSelectedOrderIds(Array.from(selectedOrderIdsSet));
  };

  return (
    <Paper>
      <FilterPanel
        key={authContext?.user?.id ?? "pending"}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
        onExport={
          authContext?.isAdmin() || authContext?.isBA()
            ? onBatchExport
            : undefined
        }
      />
      {authContext && (authContext.isAdmin() || authContext.isBA()) && (
        <>
          <input
            type="checkbox"
            checked={onlyShowMine}
            onChange={() => setOnlyShowMine(!onlyShowMine)}
          />
          <span>只显示我的订单</span>
        </>
      )}
      <Box
        sx={{ flexGrow: 1, display: "flex", alignItems: "center", gap: "20px" }}
      >
        <Button
          type="button"
          variant="contained"
          onClick={() => handleExportOrders(false)}
          disabled={selectedOrderIds.length === 0}
        >
          {authContext?.isWarehouse() ? "导出商务订单" : "导出订单"}
        </Button>
        {/*{authContext?.isWarehouse() && (*/}
        {/*  <Button*/}
        {/*    type="button"*/}
        {/*    variant="contained"*/}
        {/*    onClick={() => handleExportOrders(true)}*/}
        {/*    disabled={selectedOrderIds.length === 0}*/}
        {/*  >*/}
        {/*    导出仓库订单*/}
        {/*  </Button>*/}
        {/*)}*/}
        <Button
          type="button"
          variant="contained"
          onClick={() => handleClickCancelOrders()}
          disabled={selectedOrderIds.length === 0}
        >
          批量取消订单
        </Button>
        {(authContext?.isWarehouse() || authContext?.isAdmin()) && (
          <Button
            type="button"
            variant="contained"
            onClick={() => handleClickShipOrders()}
            disabled={selectedOrderIds.length === 0}
          >
            批量发货
          </Button>
        )}
        {(authContext?.isBA() ||
          authContext?.isAdmin() ||
          authContext?.isFinance()) && (
          <Button
            type="button"
            variant="contained"
            onClick={() => handleFetchKdStatus()}
            disabled={loading}
          >
            刷新金蝶状态
          </Button>
        )}
        {(authContext?.isBA() ||
          authContext?.isAdmin() ||
          authContext?.isFinance()) && (
          <Button
            type="button"
            variant="contained"
            onClick={() => handleClickUploadKdOrders()}
            disabled={loading || selectedOrderIds.length === 0}
          >
            批量上传金蝶
          </Button>
        )}
        {
          <div>
            <Checkbox
              checked={onlyUpload}
              onChange={(e) => setOnlyUpload(e.target.checked)}
            />
            <span>只上传不提交</span>
          </div>
        }
      </Box>
      <Table className="list-table">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <input
                type="checkbox"
                checked={currentPageAllSelected}
                onChange={handleAllSelectChange}
              />
              操作
            </TableCell>
            <TableCell
              className="sortable-column"
              onClick={() => handleSort("id")}
            >
              订单号
            </TableCell>
            <TableCell
              className="sortable-column"
              onClick={() => handleSort("salesperson")}
            >
              业务员
            </TableCell>
            {authContext && !authContext.isCustomer() && (
              <TableCell className="sortable-column">客户名</TableCell>
            )}
            <TableCell className="sortable-column">规格型号</TableCell>
            <TableCell
              className="sortable-column"
              onClick={() => handleSort("quantity")}
            >
              数量
            </TableCell>
            {!authContext?.isWarehouse() && (
              <TableCell
                className="sortable-column"
                onClick={() => handleSort("unit_price")}
              >
                单价
              </TableCell>
            )}
            <TableCell className="sortable-column">物流商</TableCell>
            <TableCell className="sortable-column">物流单号</TableCell>
            <TableCell
              className="sortable-column"
              onClick={() => handleSort("status")}
            >
              状态
            </TableCell>
            <TableCell className="sortable-column">物料名称</TableCell>
            <TableCell
              className="sortable-column"
              onClick={() => handleSort("billingName")}
            >
              收件人
            </TableCell>
            <TableCell
              className="sortable-column"
              onClick={() => handleSort("billingTel")}
            >
              手机号
            </TableCell>
            <TableCell
              className="sortable-column"
              onClick={() => handleSort("createdAt")}
            >
              创建时间
            </TableCell>
            <TableCell
              className="sortable-column"
              onClick={() => handleSort("kdStatus")}
            >
              金蝶状态
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{currentPageOrders}</TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25, 50, 100, 300, 500, 1000]}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      {selectedOrder && (
        <OrderDetail
          order={selectedOrder}
          modelOptions={queryOptions.materials}
          onClose={handleCloseDetail}
          onUpdate={() => {
            handleCloseDetail(true);
          }}
        />
      )}
      <Dialogs
        open={showDialog}
        title={dialogTitle}
        content={dialogContent}
        onConfirm={handleConfirm.current}
        onCancel={() => setShowDialog(false)}
      />
    </Paper>
  );
};
export default OrderList;
